import { FC } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import dayjs from 'dayjs'
import theme from 'theme'
import { Application, ApplicationManifest } from 'models'
import { escapeRegExp } from 'lib/utils'
import {
  AddIconWrapper,
  CardContent,
  CardHeader,
  CategoryChip,
  IntegrationCardStyled,
  LogoContainer,
  NewChip,
  NoInstancesText,
  ViewInstancesButton
} from '../../../components/integrationCard.styles'
import { AddIconTooltip } from '../../../components/AddIconTooltip'
import { AddIcon, NewChipIcon } from '../../../../../../components/svg'
import { ADD_INSTANCE_LABEL, NO_INSTANCES_TEXT } from '../applicationsCatalog.constants'
import { CatalogInstance } from '../useApplicationsCatalog'

interface ManifestCardProps {
  application?: undefined
  isNew: boolean
  instanceCount: number
  onAddInstance: (manifest: ApplicationManifest) => void
  onViewInstances: (manifest: ApplicationManifest) => void
  onOpen: (manifest: ApplicationManifest) => void
}

/* An instance card reads its logo, vendor and categories from the manifest it points at. */
interface InstanceCardProps {
  application: Application
  onOpen: (instance: CatalogInstance) => void
}

type Props = {
  manifest: ApplicationManifest
  searchValue: string
} & (ManifestCardProps | InstanceCardProps)

const ApplicationCard: FC<Props> = (props) => {
  const { manifest, searchValue } = props

  const tooltip = (text: string) => <Typography className="tooltip-text">{text}</Typography>

  const highlightMatch = (text: string) => {
    if (!searchValue) return text

    const term = searchValue.toLowerCase()
    return text.split(new RegExp(`(${escapeRegExp(searchValue)})`, 'gi')).map((part, index) =>
      part.toLowerCase() === term ? (
        <span key={index} style={{ color: theme.colors.cta }}>
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  const name = props.application ? props.application.display_name : manifest.display_name
  const instanceCount = props.application ? 0 : props.instanceCount

  return (
    <IntegrationCardStyled
      is_instance={props.application ? 'true' : 'false'}
      className="integration-card"
      onClick={() =>
        props.application ? props.onOpen({ application: props.application, manifest }) : props.onOpen(manifest)
      }
    >
      <CardHeader className="card-header">
        <Box display="flex" gap="20px" sx={{ width: '100%', overflow: 'hidden' }}>
          <LogoContainer className="logo-container">
            <img
              src={`/icons/transparent/rounded/${manifest.logo_path}`}
              alt={manifest.logo_path}
              width="56px"
              height="56px"
            />
          </LogoContainer>

          <Box sx={{ gap: '2px', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <AddIconTooltip title={tooltip(name)}>
              <Typography variant="h6" className="integration-name">
                {highlightMatch(name)}
              </Typography>
            </AddIconTooltip>

            <Typography variant="body2" className="instance-name">
              {manifest.vendor.display_name}
            </Typography>

            {props.application?.description && (
              <AddIconTooltip title={tooltip(props.application.description)}>
                <Typography variant="body2" className="instance-description">
                  {props.application.description}
                </Typography>
              </AddIconTooltip>
            )}

            {!props.application && props.isNew && (
              <NewChip>
                <NewChipIcon />
                New
              </NewChip>
            )}
          </Box>
        </Box>

        {!props.application && (
          <AddIconWrapper
            onClick={(e) => {
              e.stopPropagation()
              props.onAddInstance(manifest)
            }}
          >
            <AddIconTooltip title={tooltip(ADD_INSTANCE_LABEL)}>
              <IconButton disableFocusRipple disableRipple disableTouchRipple>
                <AddIcon />
              </IconButton>
            </AddIconTooltip>
          </AddIconWrapper>
        )}
      </CardHeader>

      <CardContent className="card-content" hasInstances={instanceCount > 0 ? 'true' : 'false'}>
        <Box className="categories-wrapper">
          {/* Chips share one row and ellipsize, so the full category stays reachable on hover. */}
          {manifest.supported_categories.map((category) => (
            <AddIconTooltip key={category} title={tooltip(category)}>
              <CategoryChip label={category} size="small" />
            </AddIconTooltip>
          ))}
        </Box>

        {props.application ? (
          <Box className="instance-footer">
            <Typography className="footer-created-at-text">
              Created: <span>{dayjs(props.application.created_at).format('MM/DD/YYYY')}</span>
            </Typography>
          </Box>
        ) : instanceCount > 0 ? (
          <ViewInstancesButton
            onClick={(e) => {
              e.stopPropagation()
              props.onViewInstances(manifest)
            }}
          >
            View {instanceCount} {instanceCount === 1 ? 'Instance' : 'Instances'}
          </ViewInstancesButton>
        ) : (
          <NoInstancesText>{NO_INSTANCES_TEXT}</NoInstancesText>
        )}
      </CardContent>
    </IntegrationCardStyled>
  )
}

export default ApplicationCard
